import { createContext, useContext } from "react";

/**
 * Which page is rendering the shared panel, for **telemetry only**.
 *
 * ## Why this exists
 *
 * The panel was extracted from My Info so User Manager could reuse it, but its *page identity* was
 * not extracted with it — so every shared component still reported itself as My Info to both
 * telemetry channels, and User Manager's numbers landed in My Info's buckets (review 2026-09-22):
 *
 * | Channel | Carried by | Reaches |
 * |---|---|---|
 * | `TabState`'s `loadingActionName` | `LoadingIndicator` | New Relic — `addPageAction("Loading Spinner Finished", { Action })`, on unmount |
 * | `Button`'s `analyticsConfiguration.eventName` | `libs/core/components`' own `Button` wrapper | GTM — `TagManager.event` |
 *
 * Neither is a DOM string and neither is asserted by any spec, so nothing surfaced the mislabelling.
 *
 * ## Telemetry ONLY — do not read this for behavior
 *
 * ⚠️ **Nothing may branch on the area.** `components/UserInfo/index.ts` states the rule this context
 * sits closest to breaking: *"Nothing here may assume **which** area is rendering it. Every component
 * takes its subject as a prop and every read hook takes `{ userId, accountId }` — that property is
 * what let the same panel serve a clicked row without a rewrite."* That still holds. This carries a
 * **label and a slug**, and the type is what keeps it that way: there is no area *identifier* to
 * compare against, so `area.slug === "myinfo"` is not a test anyone can write against it.
 *
 * Copy that differs between the two pages goes through the existing `isSubjectTheOperator` prop,
 * which says something true about the *subject* rather than about the page. Do not route copy,
 * read-only state, or a tab's availability through here.
 *
 * ## A separate context rather than widening `UserInfoStatusContext`
 *
 * That context's value is a bare function, it is already exported from the barrel (so widening it is
 * a public-API break), and coupling area identity to the live region would make the two fail
 * together — a page that forgot the status provider would also lose its telemetry identity.
 */
export interface UserInfoArea {
  /** Human-readable, for New Relic action names — e.g. `"Loading My Info feature access"`. */
  label: string;
  /** kebab slug, for GTM event names — e.g. `"myinfo-reset-password-confirm"`. */
  slug: string;
}

/**
 * Deliberately **neutral**, not My Info's values.
 *
 * Two reasons, and the first is the same one `UserInfoStatusContext`'s no-op default has: every tab
 * is also rendered in isolation by its own spec with no shell above it, so the default has to be
 * harmless rather than wrong. The second is that a My-Info-shaped default would re-assert into the
 * shared surface exactly the assumption the barrel forbids — the panel would once again *be* My Info
 * unless told otherwise.
 *
 * Both shells provide explicitly (`MyInfo.tsx`, `UserManager.tsx`), so no production value depends on
 * this. If a new page mounts the panel and forgets the provider, its telemetry reads `"User Info"` /
 * `"userinfo"` — unattributed, which is recoverable, rather than attributed to the wrong page, which
 * is not.
 */
export const DEFAULT_USER_INFO_AREA: UserInfoArea = {
  label: "User Info",
  slug: "userinfo"
};

export const UserInfoAreaContext = createContext<UserInfoArea>(
  DEFAULT_USER_INFO_AREA
);

/** The area the panel is mounted in. See the file's comment — telemetry only. */
export const useUserInfoArea = () => useContext(UserInfoAreaContext);
